import React, { useState } from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { Archive } from 'react-feather';
import { useIntl } from 'react-intl';
import cn from 'classnames';

import {
  formatDate,
  formatTime,
  getWinLossType,
  isLeague,
} from '../../lib';
import { useLocale } from '../../i18n';

import Team from '../Team';
import { useConfig } from '../ConfigContext';

const getSurfaceName = (surface, includeVenue = true) => {
  if (!surface) return;
  if (includeVenue && surface?.venue?.name && surface.venue.name !== surface.name) {
    return `${surface.venue.name} - ${surface.name}`;
  }
  return surface.name;
}

const getSurfaceCity = (surface, includeCountry = true, includeRegion = true, includeAddress = true) => {
  if (!surface || !surface.venue) return;

  let city = surface.venue.city;
  if (includeRegion && surface.venue.region && surface.venue.city != surface.venue.region) {
    city = `${city}, ${surface.venue.region}`;
  }
  if (includeCountry && surface.venue.country && surface.venue.city != surface.venue.country && city != surface.venue.country) {
    city = `${city}, ${surface.venue.country}`;
  }

  if (!surface.venue.address || !includeAddress) return city;
  return `${surface.venue.address}, ${city}`;
}

const handleClick = (history, game, hideBoxScore) => {
  const { id, isApproved } = game;
  if (!isApproved || hideBoxScore) return;

  history.push(`/games/${id}`, { modal: true });
};

const getScheduleLink = game => {
  if (!game.schedule) return;
  const { schedule, scheduleId, groupId } = game;

  if (!isLeague(schedule)) {
    const href = `/schedules/${scheduleId}`
    return groupId ? `${href}?groupId=${groupId}` : href;
  }

  const href = `/leagues/${schedule.officeId}?scheduleId=${scheduleId}`
  return groupId ? `${href}&groupId=${groupId}` : href;
}

const GameMeta = ({ game, hideDate, hideScheduleName }) => {
  const [ locale ] = useLocale();
  const { seasonId } = useConfig();
  const { formatMessage } = useIntl();

  const { schedule, startTime, timezone, number, group } = game;
  const date = !hideDate ? ` · ${formatDate(startTime, locale, timezone)}` : null
  if (!schedule) return <div className="text-muted">{number}{date}</div>;

  const href = getScheduleLink(game);

  let { name } = schedule;
  if (isLeague(schedule)) {
    name = `${formatMessage({ id: `common.enums.scheduletypes.${schedule.type}` })} ${name}`
  }
  if (hideScheduleName) {
    name = schedule.type === 'League' ?
      formatMessage({ id: 'common.lang.regularseason' }) :
      formatMessage({ id: `common.enums.scheduletypes.${schedule.type}` });
  }
  if (group) name += ` · ${group.name}`;

  const archive = game.seasonId !== seasonId && <span title={formatMessage({ id: 'common.lang.past.season' })}><Archive size={14} className="mr-1" /></span>

  return <div className="game-meta text-muted">
    {archive} {number}{date}&nbsp;&middot;&nbsp;
    <Link to={href} className="text-muted" title={name} onClick={e => e.stopPropagation()}>{name}</Link>
  </div>
}

const GameTime = ({ game }) => {
  const [ locale ] = useLocale()
  const { formatMessage } = useIntl();

  const isIncomplete = !game?.teamStats?.length // stats may be submitted without an approved scoresheet
  const time = isIncomplete ?
    `${formatTime(game.startTime, locale, game.timezone)} - ${formatTime(game.endTime, locale, game.timezone, true)}` :
    formatMessage({ id: getWinLossType(game.teamStats) });
  const status = !game.isApproved && game.status !== 'Active' ? game.status : null;
  return <div className="game-time">
    {time}
    {status && <div className="game-status">{formatMessage({ id: `common.enums.gamestatus.${status}` })}</div>}
  </div>
}

const GameSurface = ({ game }) => {
  const { hideKraft } = useConfig();
  const surface = game.surface;
  const name = getSurfaceName(surface);
  const city = getSurfaceCity(surface, false, true, false);
  return <div className="game-surface">
    <span>
      {name}<span className="text-muted"> &middot; {city}</span>
    </span>
    {!hideKraft && <a title="Kraft Hockeyville 2023" href={`https://www15.smartadserver.com/h/cc?imgid=29352598&tmstp=${Date.now()}&tgt=play-${game.arenaId}`} target="_blank" rel="noopener noreferrer">
      <img src="kraft-hockeyville.png" className="kraft" />
    </a>}
  </div>
}

const GameComments = ({ game }) => {
  const [ expanded, setExpanded ] = useState(false);
  const { formatMessage } = useIntl();
  if (!game.comments) return null;

  let comments = game.comments.trim()
  const long = comments.length > 200;
  const minimized = long && !expanded;

  const classNames = cn('game-comments', minimized ? 'minimized' : undefined);
  const onClick = e => {
    if (!long || expanded) return;
    e.preventDefault();
    setExpanded(true);
  }

  if (minimized) {
    comments = `${game.comments.substring(0, 200)}...\n(${formatMessage({ id: 'components.gamelist.tap.to.read.more' })})`
  }

  return <div className={classNames} onClick={onClick}>{comments}</div>
}

const GameResult = ({ game, reverseTeams }) => {
  const vs = <div className="game-result vs">{reverseTeams ? 'VS' : '@'}</div>
  if (!game || !game.isApproved || !game.teamStats) return vs

  const home = game.teamStats.find(stats => stats.teamId == game.homeTeamId)
  const away = game.teamStats.find(stats => stats.teamId == game.awayTeamId)
  const left = reverseTeams ? home : away;
  const right = reverseTeams ? away : home;

  if (!left || !right) return vs;
  return <div className="game-result score">
    <div className={`result result-${left.gameResult}`}>{left.goalFor}</div>
    <span className="text-muted"> - </span>
    <div className={`result result-${right.gameResult}`}>{right.goalFor}</div>
  </div>
}

const BaseTableRow = ({ isMobile, game, hideDate, hideScheduleName, history }) => {
  const { reverseTeams, hideBoxScore } = useConfig();
  const leftTeam = reverseTeams ? game.homeTeam : game.awayTeam;
  const rightTeam = reverseTeams ? game.awayTeam : game.homeTeam

  return <tr className={cn('gamelist-row', { approved: game.isApproved && !hideBoxScore })} onClick={() => handleClick(history, game, hideBoxScore)}>
    <td className="game-details">
      <GameMeta game={game} hideDate={hideDate} hideScheduleName={hideScheduleName} />
      <GameTime game={game} />
      <GameSurface game={game} />
      <GameComments game={game} />
    </td>
    <td>
      <div className="game-matchup">
        <Team team={leftTeam} scheduleId={game.scheduleId} groupId={game.groupId} style="full" />
        <GameResult game={game} reverseTeams={reverseTeams} />
        <Team team={rightTeam} scheduleId={game.scheduleId} groupId={game.groupId} style="full" reverse={!isMobile} />
      </div>
    </td>
  </tr>
}

export const TableHeader = () => null
export const TableRow = withRouter(BaseTableRow);
